import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { GithubUserModel } from 'src/app/Models/github-user-model';
import { GithubIntegrationService } from 'src/app/Services/github-integration.service';
import { NotifyService } from 'src/app/Services/notify.service';
import { SharedService } from 'src/app/Services/shared.service';

@Component({
  selector: 'ze-github-integration-view',
  templateUrl: './github-integration-view.component.html',
  styleUrls: ['./github-integration-view.component.scss']
})
export class GithubIntegrationViewComponent implements OnInit {

  returnUrl: string;
  user: string = "";
  githubUserData: GithubUserModel;
  isGithubIntegrationExists: boolean = false;
  githubIntegrationUrl: string = null;
  currentEpochTime: number = Date.now();
  updateFailed: boolean = false;
  deleteWarningMessage: string = ""; // Add this at the top of your class

  private subscriptions: Subscription = new Subscription();

  constructor(private route: ActivatedRoute,
    private githubIntegrationService: GithubIntegrationService,
    private sharedService: SharedService,
    private notifyService: NotifyService,
    private router: Router,) {
    let currentUser1 = JSON.parse(localStorage.getItem("currentUser")).token;
    this.user = this.parseJwt(JSON.stringify(currentUser1)).sub;
  }

  ngOnInit() {
    this.sharedService.loaderHide.emit(true);
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/project';
    this.subscriptions.add(this.route.queryParams.subscribe(params => {
      if (params["installation_id"]) {
        this.githubIntegrationService.addUserByInstallationId(params["installation_id"], params["code"]).subscribe(data => {
          if (data["data"] == null || data["operationStatus"] == "ERROR") {
            this.isGithubIntegrationExists = false;
            this.getGithubIntegrationUrl();
            if (data["operationMessage"] != null && data["operationMessage"].includes("Not Updated")) {
              this.notifyService.notify('error', 'Failed', data["operationMessage"]);
              this.updateFailed = true;
            }
          } else {
            this.isGithubIntegrationExists = true;
            this.githubUserData = data["data"];
            this.navigateToGithub();
          }
        }, error => {
          this.sharedService.loaderHide.emit(false);
        });
      } else if (params["code"]) {
        this.githubIntegrationService.updateAccessTokenByCode(params["code"]).subscribe(data => {
          if (data["data"] == null || data["operationStatus"] == "ERROR") {
            this.notifyService.notify('error', 'Failed', "Error in Updating Access Token");
          } else {
            this.isGithubIntegrationExists = true;
            this.githubUserData = data["data"];
            this.sharedService.loaderHide.emit(false);
          }
          this.navigateToGithub();
        }, error => {
          this.sharedService.loaderHide.emit(false);
        });
      } else {
        this.getGithubUserData();
      }
    }));
    this.subscriptions.add(interval(1000).subscribe(() => {
      this.currentEpochTime = Date.now();
    }));
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to avoid memory leaks
    this.subscriptions.unsubscribe();
  }

  parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  }

  integrateGithub() {
    this.sharedService.loaderHide.emit(true);
    window.location.href = this.githubIntegrationUrl;
  }

  getGithubUserData() {
    this.githubIntegrationService.getGithubUserDataByUsername().subscribe(data => {
      if (data["data"] == null || data["operationStatus"] == "ERROR") {
        this.isGithubIntegrationExists = false;
        this.getGithubIntegrationUrl();
      } else {
        this.isGithubIntegrationExists = true;
        this.githubUserData = data["data"];
        this.sharedService.loaderHide.emit(false);
      }
    }, error => {
      this.sharedService.loaderHide.emit(false);
    });
  }

  getGithubIntegrationUrl() {
    this.githubIntegrationService.getGithubIntegrationUrl().subscribe(data => {
      if (data["data"] == null || data["operationStatus"] == "ERROR") {
        this.githubIntegrationUrl = null;
      } else {
        this.githubIntegrationUrl = data["data"];
      }
      this.sharedService.loaderHide.emit(false);
    }, error => {
      this.sharedService.loaderHide.emit(false);
    });
  }

  get isRefreshTokenValid(): boolean {
    return this.githubUserData.refresh_token &&
      this.githubUserData.refresh_token_expires_at >= this.currentEpochTime;
  }

  get isAccessTokenInvalid(): boolean {
    return !this.githubUserData.user_access_token ||
      this.githubUserData.user_access_token_expires_at < this.currentEpochTime;
  }

  authenticate() {
    this.sharedService.loaderHide.emit(true);
    this.githubIntegrationService.getGithubAuthenticationUrl().subscribe(data => {
      if (data["data"] == null || data["operationStatus"] == "ERROR") {
        this.notifyService.notify('error', 'Failed', "Error in Redirecting to Authentication Url");
      } else {
        window.location.href = data["data"]
      }
    }, error => {
      this.notifyService.notify('error', 'Failed', "Error in Redirecting to Authentication Url");
      this.sharedService.loaderHide.emit(false);
    });
  }

  refreshToken() {
    this.sharedService.loaderHide.emit(true);
    this.githubIntegrationService.refreshUserAccessTokenByGithubUserModelId(this.githubUserData._id).subscribe(data => {
      if (this.githubIntegrationService.isReauthRequired(data)) {
        this.authenticate();
      } else if (data["data"] == null || data["operationStatus"] == "ERROR") {
        this.notifyService.notify('error', 'Failed', "Error in Refreshing Token");
        this.sharedService.loaderHide.emit(false);
      } else {
        this.githubUserData = data["data"];
        this.sharedService.loaderHide.emit(false);
      }
    }, error => {
      this.notifyService.notify('error', 'Failed', "Error in Refreshing Token");
      this.sharedService.loaderHide.emit(false);
    });
  }

  navigatePreviousUrl(): void {
    this.router.navigateByUrl(this.returnUrl);
  }

  navigateToGithub(): void {
    this.router.navigate([this.router.url.split('?')[0]], { queryParams: {} });
  }

  deleteIntegration() {
    this.refreshToken();
    this.sharedService.loaderHide.emit(true);
    this.githubIntegrationService.deleteGithubIntegration().subscribe(data => {
      if (
        data &&
        typeof data["data"] === "string" &&
        data["data"].includes("Github App Integration Found")
      ) {
        this.deleteWarningMessage = data["data"];
      } else if (data["operationStatus"] === "SUCCESS") {
        this.deleteWarningMessage = "";
        this.notifyService.notify('success', 'Success', 'Integration deleted successfully.');
        this.getGithubUserData(); // Optionally refresh the view
      } else {
        this.deleteWarningMessage = "Failed to delete integration.";        
        this.getGithubUserData();
      }
      this.sharedService.loaderHide.emit(false);
    }, error => {
      this.deleteWarningMessage = "Error deleting integration.";
      this.sharedService.loaderHide.emit(false);
    });
  }

}
